<template>
  <div>
    <a-form
      ref="formRef"
      class="form selectors"
      :model="formState"
      :rules="rules"
      layout="vertical"
    >
      <a-form-item label="Selecciona una opcion" name="option">
        <a-radio-group v-model:value="formState.option">
          <a-radio :value="1">Detallado</a-radio>
          <a-radio :value="2">Sumarizado</a-radio>
        </a-radio-group>
      </a-form-item>
      <PlanSeparatedDatePicker
        v-model:startDate="formState.startValue"
        v-model:endDate="formState.endValue"
        start-name="startValue"
        end-name="endValue"
        :format="isDetailed ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'"
        valueFormat="YYYY-MM-DD HH:mm:ss"
        :show-time="isDetailed"
        startPlaceholder="-- / -- / ----"
        endPlaceholder="-- / -- / ----"
      />
      <div v-if="formState.option" class="back">
        <span class="total" v-if="countReport > -1 && formState.option === 2">
          {{ countReport }} Registros
        </span>
        <span v-if="detailCounterPlatform > -1 && isDetailed" class="total">
          {{ detailCounterPlatform }} Registros
        </span>

        <a-tooltip :title="isGlobalDownloading ? 'Tiene una descarga pendiente' : null">
          <a-button
            :type="formState.option == 1 ? 'primary' : 'link'"
            :disabled="!isDisabled || isGlobalDownloading"
            @click="handleDownload"
            :loading="isLoading"
          >
            Descargar
            <DownloadOutlined />
          </a-button>
        </a-tooltip>

        <a-button
          v-if="formState.option == 2"
          type="primary"
          :disabled="!isDisabled"
          @click="handleSearch"
          :loading="isLoading"
        >
          Buscar
          <SearchOutlined />
        </a-button>
      </div>
    </a-form>
    <div v-if="formState.option == 2">
      <TableSkeleton v-show="isLoading" width="200px" />
      <a-table
        size="middle"
        :columns="columnsTable"
        :data-source="reportTimePlatform?.items"
        :scroll="scrollStyle"
        :pagination="reportTimePlatform?.pagination"
        @change="handleTableChange"
        v-show="!isLoading"
      >
        <template #annex="{ text }">
          <span>{{ text.split("@")[0] }}</span>
        </template>

        <template #date="{ text }">
          {{ dateTimeToSlashFormat(text, false) }}
        </template>

        <template #customPortfolio="{ record }">
          <span v-if="record.portfolio">{{ record.portfolio }}</span>
          <span v-if="!record.portfolio">Sin Campaña</span>
        </template>

        <template #connected>
          {{ StateTexts[StateOptions.Connected] }}
          <a-tooltip>
            <template #title> Sumatoria de todos los eventos de llamada y mensajería </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #titleLine>
          {{ EventData[EventManagement.Online].name }}
          <a-tooltip>
            <template #title> Conectado sin ninguna estrategia asignada o llamada activa </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #titleWaiting>
          {{ EventData[EventManagement.Waiting].name }}
          <a-tooltip>
            <template #title>
              Evento exclusivo para estrategias de predictivo. Es el tiempo de espera de una llamada
              contestada.
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <!-- <template #titleOpening>
          {{ EventData[EventManagement.Opened].name }}
          <a-tooltip>
            <template #title>
              Tiempo en el que un usuario se encuentra con un ticket de mensajería abierto
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </template> -->

        <template #titleWriting>
          {{ EventData[EventManagement.Writing].name }}
          <a-tooltip>
            <template #title>
              Tiempo en el que un usuario se encuentra escribiendo un mensaje
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </template>

        <template #titleSolving>
          {{ EventData[EventManagement.Resolved].name }}
          <a-tooltip>
            <template #title>
              Tiempo en el que un usuario se encuentra resolviendo un ticket de mensajería.
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </template>
      </a-table>
    </div>
    <div v-else class="image-center">
      <div class="main-text">Este reporte solo se podrá descargar al aplicar los filtros</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, reactive, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";

import { downloadReportTimePlatform, downloadReportTimeSumarizado } from "@/app/reports/services";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { EventData } from "@/app/shared/utils/globalVariables";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";
import { StateOptions, StateTexts, EventManagement } from "@/app/shared/utils/enums";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import TableSkeleton from "@/app/shared/components/skeletons/TableSkeleton.vue";
import PlanSeparatedDatePicker from "@/app/shared/components/pickers/PlanSeparatedDatePicker.vue";

const columns = [
  {
    title: "Fecha",
    dataIndex: "date",
    key: "date",
    slots: { customRender: "date" },
  },
  {
    title: "Anexo",
    dataIndex: "annex",
    key: "annex",
    width: 130,
  },
  {
    title: "Usuario",
    dataIndex: "user",
    key: "user",
    width: 230,
  },
  {
    title: "Perfil",
    dataIndex: "profile",
    key: "profile",
    width: 160,
  },
  {
    dataIndex: "connected_time",
    key: "connected_time",
    width: 180,
    slots: { title: "connected" },
  },
  {
    dataIndex: "online_time",
    key: "online_time",
    slots: { title: "titleLine" },
  },

  {
    dataIndex: "waiting_time",
    key: "waiting_time",
    slots: { title: "titleWaiting" },
  },
  {
    dataIndex: "ringing_time",
    key: "ringing_time",
    title: EventData[EventManagement.Ringing].name,
  },
  {
    dataIndex: "spoken_time",
    key: "spoken_time",
    title: EventData[EventManagement.Talking].name,
  },
  {
    dataIndex: "acw_time",
    key: "acw_time",
    title: EventData[EventManagement.Acw].name,
  },
  // {
  //   dataIndex: "opening_time",
  //   key: "opening_time",
  //   slots: { title: "titleOpening" },
  // },
  {
    dataIndex: "writing_time",
    key: "writing_time",
    slots: { title: "titleWriting" },
    width: 130,
  },
  // { dataIndex: "typification_time", key: "typification_time", slots: { title: "titleTyping" } },
  {
    dataIndex: "closing_time",
    key: "closing_time",
    slots: { title: "titleSolving" },
    width: 130,
  },
  {
    dataIndex: "snack_time",
    key: "snack_time",
    title: StateTexts[StateOptions.Refreshment],
  },
  {
    dataIndex: "dinner_time",
    key: "dinner_time",
    title: StateTexts[StateOptions.Dinner],
  },
  {
    dataIndex: "toilet_time",
    key: "toilet_time",
    title: StateTexts[StateOptions.Services],
    width: 130,
  },
  {
    dataIndex: "busy_time",
    key: "busy_time",
    title: StateTexts[StateOptions.Occupied],
  },
  {
    dataIndex: "training_time",
    key: "training_time",
    title: StateTexts[StateOptions.Training],
    width: 130,
  },
];

export default defineComponent({
  components: {
    DownloadOutlined,
    SearchOutlined,
    InfoCircleOutlined,
    TableSkeleton,
    PlanSeparatedDatePicker,
  },
  setup() {
    const store = useStore();
    const isLoading = ref(false);
    const formRef = ref();
    const formState = reactive({
      option: undefined,
      startValue: "",
      endValue: "",
    });
    const rules = {
      option: [],
      startValue: [{ required: true, message: "Campo requerido" }],
      endValue: [{ required: true, message: "Campo requerido" }],
    };

    const isDetailed = computed(() => formState.option === 1);

    const isDisabled = computed(() => {
      return formState.startValue && formState.endValue && formState.option;
    });

    const reportTimePlatform = computed(() => store.getters["getReportTimePlatformat"]);
    const countReport = computed(() => reportTimePlatform.value?.pagination?.total);
    const columnsTable = computed(() => columns);
    const detailCounterPlatform = computed(() => store.getters["getDetailReportTime"]?.count);

    const { scrollStyle } = useScrollResize({ headerMargin: 450, width: 2500 });
    const { isGlobalDownloading } = useGlobalDownloadState();

    const handleDownload = async () => {
      try {
        if (formState.option === 1) {
          isLoading.value = true;
          await downloadReportTimePlatform({
            startValue: formState.startValue,
            endValue: formState.endValue,
          });
          isLoading.value = false;
        } else if (formState.option == 2) {
          isLoading.value = true;
          await downloadReportTimeSumarizado({
            startValue: formState.startValue,
            endValue: formState.endValue,
          });
          isLoading.value = false;
        }
      } catch (error) {
        console.log("Error");
      }
    };

    const handleSearch = async () => {
      isLoading.value = true;
      try {
        await store.dispatch("fetchReportTimePlatform", {
          query: {
            startValue: formState.startValue,
            endValue: formState.endValue,
          },
          pagination: {},
        });
      } catch (error) {
        console.error("Error", error);
      }
      isLoading.value = false;
    };

    const handleTableChange = async (pagination) => {
      await store.dispatch("fetchReportTimePlatform", {
        query: {
          startValue: formState.startValue,
          endValue: formState.endValue,
        },
        pagination: pagination,
      });
    };
    // const handleSearchDetail = () => {
    //   if (!formState.startValue || !formState.endValue) return;

    //   store.dispatch("fetchAllDatailTimePlatform", {
    //     startValue: formState.startValue,
    //     endValue: formState.endValue,
    //   });
    // };

    watch(
      () => formState.option,
      () => {
        store.commit("SET_REPORT_TIME_PLATFORM", null);
      }
    );

    watch(formState, () => {
      if (!formState.startValue || !formState.endValue || !formState.option) return;

      store.dispatch("fetchAllDatailTimePlatform", {
        startValue: formState.startValue,
        endValue: formState.endValue,
      });
    });

    onMounted(() => {
      store.commit("SET_REPORT_DETAIL_TIME_PLATFORM", null);
    });

    return {
      StateOptions,
      StateTexts,
      dateTimeToSlashFormat,
      isDetailed,
      scrollStyle,
      formRef,
      formState,
      rules,
      isDisabled,
      countReport,
      handleDownload,
      handleSearch,
      handleTableChange,
      reportTimePlatform,
      columnsTable,
      detailCounterPlatform,
      isLoading,
      EventData,
      EventManagement,
      isGlobalDownloading,
    };
  },
});
</script>

<style lang="sass" scoped>
.selectors
  display: grid
  grid-template-columns: 230px 190px 190px 2fr
  gap: 15px
  padding: 15px 5px 5px
:deep(.ant-calendar-picker-icon)
  top: 50% !important
:deep(.ant-calendar-picker)
  width: 100%
.back
  margin-top: 30px
  text-align: right
.main-text
  font-size: 14px
  font-style: normal
  font-weight: 400
  line-height: 28px
  letter-spacing: 0em
  text-align: center
  color: $gray-7
.total
  margin-top: 3%
  color: $gray-8
  padding-right: 15px
</style>
